import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import StyledModalFooter from "@coworker/apprestructured/src/task-management/components/StyledModalFooter/StyledModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { useTranslation } from "react-i18next";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";

interface ConfirmSheetModalProps {
  content: React.ReactNode;
  showModal: boolean;
  handleCloseModal: () => void;
  handleConfirm: () => void;
  title?: string;
}

/**
 * Generic modal wrapper component using FixaSheets pattern
 *
 * Displays any content in a full-screen sheet modal with a confirm action button.
 * Use this component when you need a modal that:
 * - Shows custom content (passed as ReactNode)
 * - Has a confirm/submit action
 * - Uses the full-screen sheets pattern
 *
 * @param content - Any React content to display in the modal body
 * @param showModal - Controls modal visibility
 * @param handleCloseModal - Called when modal is closed (via close button or backdrop)
 * @param handleConfirm - Called when confirm button is clicked
 * @param title - Optional title shown in the modal header
 */
export const ConfirmSheetModal = ({
  content,
  showModal,
  handleCloseModal,
  handleConfirm,
  title,
}: ConfirmSheetModalProps) => {
  const { t } = useTranslation();
  return (
    <FixaModal visible={showModal} handleCloseBtn={handleCloseModal}>
      <FixaSheets
        labelledById="confirm-sheet-modal"
        fullSize
        header={
          <FixaModalHeader
            id="confirm-sheet-modal-header"
            closeBtnClick={handleCloseModal}
            title={title}
          />
        }
        footer={
          <StyledModalFooter>
            <FixaButton
              text={t("confirmString")}
              type="primary"
              onClick={handleConfirm}
            />
          </StyledModalFooter>
        }
      >
        <FixaModalBody>{content}</FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
